import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
  Error,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import {
  useCreateCompanyPayment,
  useCreatePaymentProfile,
  useUpdateCompanyPayment,
} from "../../queries/payment";
import { Select } from "../../components/shared/Select";

export interface CompanyPaymentDetails {
  name?: string;
  identifier?: string;
  payment_gateway?: string;
}

type PaymentDetailsDialogProps = {
  onClose: () => void;
  data?: CompanyPaymentDetails;
  mode?: "create" | "edit";
};

const initialValues = {
  name: "",
  identifier: "",
};

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters long")
    .max(50, "Name must not exceed 50 characters"),
  identifier: Yup.string()
    .required("Identifier is required")
    .min(2, "Identifier must be at least 2 characters long")
    .max(50, "Identifier must not exceed 50 characters"),
});

const PaymentDetailsDialog = ({
  onClose,
  data,
  mode,
}: PaymentDetailsDialogProps) => {
  const { mutate: createPayment, isLoading: isCreating } =
    useCreatePaymentProfile();

  const formik = useFormik({
    initialValues: data
      ? {
          name: data.name || "",
          identifier: data.identifier || "",
          payment_gateway: data.payment_gateway || "easebuzz",
        }
      : initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (mode === "create") {
        createPayment(values, {
          onSuccess: () => {
            toast("Payment Profile Created Successfully", {
              type: "success",
              autoClose: 2000,
            });
            onClose();
          },
          onError: (error) => {
            toast("Error Creating Payment Profile", {
              type: "error",
              autoClose: 2000,
            });
          },
        });
      } else {
        // Handle update
      }
    },
  });

  return (
    <VStack className="min-h-min">
      <div className="p-4 text-lg font-medium border-b ">
        <h6 className="flex justify-between">
          Payment Details
          <span className="cursor-pointer" onClick={onClose}>
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full gap-2 p-4">
        <HStack className="gap-4">
          <div className="flex-1">
            <Label className="text-sm font-normal">Payment Gateway</Label>
            <Select
              name="payment_gateway"
              value={data?.payment_gateway || "easebuzz"}
              disabled
              onChange={() => {}}
              onBlur={() => {}}
              options={["easebuzz", "juspay"]}
            />
          </div>
        </HStack>
        <HStack className="gap-4">
          <div className="flex-1">
            <Label className="required text-sm font-normal">Profile Name</Label>
            <Input
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter profile name"
            />
            {formik.touched.name && formik.errors.name && (
              <Error text={formik.errors.name} />
            )}
          </div>
          <div className="flex-1">
            <Label className="required text-sm font-normal">
              PG Identifier
            </Label>
            <Input
              name="identifier"
              value={formik.values.identifier}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter PG Identifier"
            />
            {formik.touched.identifier && formik.errors.identifier && (
              <Error text={formik.errors.identifier} />
            )}
          </div>
        </HStack>
      </VStack>
      <HStack className="justify-between px-4 py-6 mt-7">
        <ButtonSecondary
          onClick={onClose}
          className="text-gray-400 bg-slate-50"
        >
          Cancel
        </ButtonSecondary>
        <ButtonPrimary
          loading={isCreating}
          onClick={() => formik.handleSubmit()}
        >
          Save
        </ButtonPrimary>
      </HStack>
    </VStack>
  );
};

export default PaymentDetailsDialog;
